import * as Sentry from '@sentry/react';

type LogLevel = 'error';

class LoggerService {
    private static instance: LoggerService;

    private constructor() {
        Sentry.init({
            dsn: "https://7a1efd0838c891c170f9a0946a12401b@o4507594091659264.ingest.de.sentry.io/4507596703662160",
            integrations: [
                Sentry.browserTracingIntegration(),
                Sentry.replayIntegration(),
            ],
            tracesSampleRate: 1.0,
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
            ignoreErrors: [
                // Browser extension noise — thrown when an extension's content script
                // tries to message a background script whose tab context is gone.
                // Common on Mobile Safari / iOS where extensions are aggressively suspended.
                'Invalid call to runtime.sendMessage(). Tab not found.',
                'Cannot parse given Error object',

                // Browser extension noise — extensions like Phantom wallet inject
                // inpage.js scripts that can throw when DOM elements are removed
                // during React route transitions.
                'removeListener',
            ],

            beforeSend(event, hint) {
                const error = hint?.originalException as any;

                // Drop chunk load errors — handled by the reload logic in index.tsx
                if (error?.name === 'ChunkLoadError' || error?.name === 'Sd') {
                    return null;
                }

                // Drop non-error promise rejections (third-party script noise, e.g. Google SODAR)
                if (event.exception?.values?.[0]?.value?.includes('Non-Error promise rejection')) {
                    return null;
                }

                // Drop errors originating from browser extensions (e.g. Phantom wallet, MetaMask).
                // Extensions inject content scripts (chrome-extension://...) into the page's JS
                // runtime. When they throw during React route transitions, Sentry picks them up.
                const frames = event.exception?.values?.[0]?.stacktrace?.frames;
                if (frames?.some((f: any) => f.filename?.startsWith('chrome-extension://'))) {
                    return null;
                }

                return event;
            },
        });
    }

    public static getInstance(): LoggerService {
        if (!LoggerService.instance) {
            LoggerService.instance = new LoggerService();
        }
        return LoggerService.instance;
    }

    private log(level: LogLevel, error?: any) {
        switch (level) {
            case 'error':
                Sentry.captureException(error);
                break;
        }

    }

    error(error?: any) {
        this.log('error', error);
    }
}

const loggerService = LoggerService.getInstance();

export default loggerService;